/**
 * Opengater Payments (8006) admin hooks — orders (list/refund) + payment
 * systems (list/update). Paths `/api/v1/admin/...`.
 */
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiOg } from './client';
// ─── Orders ─────────────────────────────────────────────────────────────────
const ORDERS = '/api/v1/admin/payments';
export function useOgOrders(params) {
    const qs = new URLSearchParams();
    for (const key of [
        'order_id',
        'payment_id',
        'provider_charge_id',
        'user_id',
        'created_from',
        'created_to',
    ]) {
        const value = params[key]?.trim();
        if (value)
            qs.set(key, value);
    }
    if (params.state)
        qs.set('state', params.state);
    const paymentSystem = params.payment_system?.trim() || params.system?.trim();
    if (paymentSystem)
        qs.set('payment_system', paymentSystem);
    qs.set('limit', String(params.limit));
    qs.set('offset', String(params.offset));
    return useQuery({
        queryKey: ['og', 'orders', params],
        queryFn: () => apiOg('payments', `${ORDERS}?${qs.toString()}`),
        // Paging and filters keep the old page on screen until the new one arrives.
        placeholderData: keepPreviousData,
        retry: false,
    });
}
export function useOgOrder(orderId) {
    return useQuery({
        queryKey: ['og', 'order', orderId],
        queryFn: () => apiOg('payments', `${ORDERS}/${encodeURIComponent(orderId ?? '')}`),
        enabled: Boolean(orderId),
        retry: false,
    });
}
export function useOrderSearchPaymentSystems() {
    return useQuery({
        queryKey: ['og', 'order-search-payment-systems'],
        queryFn: () => apiOg('payments', `${ORDERS}/payment-systems`),
        retry: false,
    });
}
export function useRefundOrder() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (orderId) => apiOg('payments', `${ORDERS}/${encodeURIComponent(orderId)}/refund`, {
            method: 'POST',
        }),
        onSuccess: (_data, orderId) => Promise.all([
            qc.invalidateQueries({ queryKey: ['og', 'orders'] }),
            qc.invalidateQueries({ queryKey: ['og', 'order', orderId] }),
        ]),
    });
}
// ─── Payment systems ────────────────────────────────────────────────────────
const SYSTEMS = '/api/v1/admin/payment-systems';
const SYS_KEY = ['og', 'payment-systems'];
export function useOgPaymentSystems() {
    return useQuery({
        queryKey: SYS_KEY,
        queryFn: () => apiOg('payments', SYSTEMS),
    });
}
export function useCreatePaymentSystem() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (body) => apiOg('payments', SYSTEMS, { method: 'POST', body }),
        onSuccess: () => qc.invalidateQueries({ queryKey: SYS_KEY }),
    });
}
export function useUpdatePaymentSystem(code) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (patch) => apiOg('payments', `${SYSTEMS}/${encodeURIComponent(code)}`, {
            method: 'PATCH',
            body: patch,
        }),
        onSuccess: () => qc.invalidateQueries({ queryKey: SYS_KEY }),
    });
}
// ─── Store products ──────────────────────────────────────────────────────────
const productKey = (code) => ['og', 'payment-systems', code, 'products'];
const productsPath = (code) => `${SYSTEMS}/${encodeURIComponent(code)}/products`;
export function usePaymentProducts(code) {
    return useQuery({
        queryKey: productKey(code),
        queryFn: () => apiOg('payments', productsPath(code)),
        enabled: code.length > 0,
    });
}
export function useCreatePaymentProduct(code) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (body) => apiOg('payments', productsPath(code), { method: 'POST', body }),
        onSuccess: () => qc.invalidateQueries({ queryKey: productKey(code) }),
    });
}
export function useUpdatePaymentProduct(code) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ id, patch }) => apiOg('payments', `${productsPath(code)}/${id}`, {
            method: 'PATCH',
            body: patch,
        }),
        onSuccess: () => qc.invalidateQueries({ queryKey: productKey(code) }),
    });
}
export function useDeletePaymentProduct(code) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (id) => apiOg('payments', `${productsPath(code)}/${id}`, { method: 'DELETE' }),
        onSuccess: () => qc.invalidateQueries({ queryKey: productKey(code) }),
    });
}
